/**
 * THE CROP'S ARITHMETIC — pure, so it is tested without a canvas.
 *
 * A picture is cropped in the browser before it is uploaded (features/home-v2/crop).
 * Two decisions are made here: what format the cropped file is written in, and
 * how large the canvas may be. A phone's canvas has a hard ceiling (iOS Safari
 * refuses past ~16.7 million pixels and draws nothing at all), so a crop of a
 * 48-megapixel photo is scaled down to fit — never up — keeping its aspect.
 */

/** Pictures the crop takes. An animated GIF would lose its motion and an SVG its vectors, so both go up as they are. */
export function isCroppable(type: string): boolean {
  // HEIC is left out: only Safari decodes it, so elsewhere the cropper would show nothing.
  return /^image\/(jpeg|jpg|png|webp|bmp|avif)$/i.test(type);
}

/** The cropped file keeps a lossless or modern format where it had one; anything else becomes JPEG. */
export function cropOutputType(type: string): "image/png" | "image/webp" | "image/jpeg" {
  if (/^image\/png$/i.test(type)) return "image/png";
  if (/^image\/webp$/i.test(type)) return "image/webp";
  return "image/jpeg";
}

export const CROP_MAX_SIDE = 4096;
export const CROP_MAX_PIXELS = 16_000_000;

/** The output size for a crop of `width × height` source pixels: whole pixels, within both ceilings, never enlarged. */
export function cropOutputSize(width: number, height: number, maxSide = CROP_MAX_SIDE, maxPixels = CROP_MAX_PIXELS): { width: number; height: number } {
  const w = Math.max(1, Math.round(width));
  const h = Math.max(1, Math.round(height));
  const scale = Math.min(1, maxSide / Math.max(w, h), Math.sqrt(maxPixels / (w * h)));
  return { width: Math.max(1, Math.floor(w * scale)), height: Math.max(1, Math.floor(h * scale)) };
}

/** The bounding box of a `width × height` picture turned by `degrees`. */
export function rotatedBox(width: number, height: number, degrees: number): { width: number; height: number } {
  const r = (degrees * Math.PI) / 180;
  return {
    width: Math.abs(Math.cos(r) * width) + Math.abs(Math.sin(r) * height),
    height: Math.abs(Math.sin(r) * width) + Math.abs(Math.cos(r) * height),
  };
}

/** The cropped file's name: the original's, with the extension of the format it is now in. */
export function cropFileName(name: string, type: string): string {
  const ext = type === "image/png" ? "png" : type === "image/webp" ? "webp" : "jpg";
  const base = name.replace(/\.[^./\\]+$/, "") || "image";
  return `${base}.${ext}`;
}

/**
 * Each slot's frame, from where its picture is shown. A banner is drawn from
 * ~2.1:1 (a phone) to ~4.8:1 (a wide desktop) and always cropped to cover, so
 * it is cut at 3:1 — the 1500 × 500 the forms already suggest — and keeps its
 * middle on every screen. A logo and a portrait are square; an auction's
 * picture is a square tile; a gallery hangs a 16:9 canvas.
 */
export const CROP_ASPECT = { banner: 3, square: 1, gallery: 16 / 9 } as const;
